import {
  displayAutoId,
  returnFieldInstanceIdsMatchingSearchTerm,
} from 'lib/fields';
import {
  arrayUnique,
  GENERICKEY_FIELDREPEATABLELIST,
  getSearchTermsFromSearchValue,
} from 'lib/riskbase';
import { orderBy } from 'lodash';
import { createCachedSelector } from 're-reselect';
import { createSelector } from 'reselect';
import { selectGeneric } from 'store/app/selectors';
import {
  AbridgedAsset,
  Asset,
  AssetPlan,
  AssetsFilter,
  AssetsSortOrder,
  AssetsState,
  File,
} from 'store/assets/types';
import { filterAndSortAssets } from 'store/assets/utilities';
import {
  selectChangedAsset,
  selectChangedAssets,
} from 'store/changes/selectors';
import {
  selectOrganisationFieldGroup,
  selectOrganisationFieldGroups,
  selectOrganisationsList,
} from 'store/organisations/selectors';
import { PinNewWithName } from 'store/plan/types';
import { State } from 'store/types';
import {
  FieldRepeatableOrderConfig,
  FieldRepeatableOrderEnum,
  FieldInstanceWithFormattedAutoId,
  FieldResult,
  UUID,
} from 'types/types';
import deepMerge from 'deepmerge';

type AssetsStateTree = Omit<Partial<State>, 'assets'> & Pick<State, 'assets'>;

const selectAssetsState = (state: AssetsStateTree): AssetsState => state.assets;

/** ASSET LIST */

export const selectAssetListUnmerged = createSelector(
  selectAssetsState,
  (assets: AssetsState): Record<string, AbridgedAsset> => assets.assets_list,
);

export const selectAssetIds = createSelector(
  selectAssetListUnmerged,
  (assets: Record<string, AbridgedAsset>) => Object.keys(assets),
);

export const selectAssetsTotal = createSelector(
  selectAssetIds,
  (assetIds) => assetIds.length,
);

export const selectAssetOrganisationIds = createSelector(
  selectAssetListUnmerged,
  (assets) =>
    arrayUnique(Object.values(assets).map((asset) => asset.organisation_id)),
);

export const selectAssetListAvatarIds = createSelector(
  selectAssetListUnmerged,
  (assets): string[] =>
    Object.values(assets).reduce<string[]>((acc, { details: { avatar } }) => {
      if (avatar) {
        acc.push(avatar);
      }
      return acc;
    }, []),
);

export const selectAssetListForOrganisation = createCachedSelector(
  selectAssetListUnmerged,
  selectChangedAssets,
  (_: AssetsStateTree, organisationId: string) => organisationId,
  (assets, changes, organisationId): AbridgedAsset[] => {
    return Object.values(assets)
      .filter((asset) => asset.organisation_id === organisationId)
      .map<AbridgedAsset>((asset) =>
        changes[asset.id]
          ? deepMerge(asset, changes[asset.id]!, {
              arrayMerge: (_, a) => a,
            })
          : asset,
      );
  },
)((_: AssetsStateTree, organisationId: string) => organisationId);

export const selectAssetListItemUnmerged = createCachedSelector(
  selectAssetListUnmerged,
  (_: AssetsStateTree, id: string) => id,
  (
    assetList: Record<string, AbridgedAsset>,
    id: string,
  ): AbridgedAsset | undefined => assetList[id],
)((_: AssetsStateTree, id: string) => id);

export const selectAssetListItem = createCachedSelector(
  selectAssetListItemUnmerged,
  selectChangedAsset,
  (asset, changes): AbridgedAsset | undefined =>
    asset && changes
      ? deepMerge(asset, changes, {
          arrayMerge: (_, a) => a,
        })
      : asset,
)((_: AssetsStateTree, id: string) => id);

export const selectAssetOrganisationId = createCachedSelector(
  selectAssetListItemUnmerged,
  (asset): string => asset?.organisation_id || '0',
)((_: AssetsStateTree, id: string) => id);

/** LOADED ASSETS */

const selectAssets = createSelector(
  selectAssetsState,
  (assetsState): AssetsState['loaded'] => assetsState.loaded,
);

export const selectIsAssetLoaded = createCachedSelector(
  selectAssets,
  (_: AssetsStateTree, id: string) => id,
  (assets, id) => !!assets[id],
)((_: AssetsStateTree, id: string) => id);

const selectAssetUnmerged = createCachedSelector(
  selectAssets,
  (_: AssetsStateTree, id: string) => id,
  (assets, id) => {
    if (!assets[id]) {
      throw new Error(
        'Asset must be loaded from local storage before selecting it',
      );
    }
    return assets[id]!;
  },
)((_: AssetsStateTree, id: string) => id);

export const selectAsset = createCachedSelector(
  selectAssetUnmerged,
  selectChangedAsset,
  (asset, changes): Asset =>
    changes ? deepMerge(asset, changes, { arrayMerge: (_, a) => a }) : asset,
)((_: AssetsStateTree, id: string) => id);

export const selectAssetFiles = createCachedSelector(
  selectAsset,
  (asset): Record<string, File> =>
    Object.fromEntries(
      Object.entries(asset.files).filter(([, file]) => !!file),
    ) as Record<string, File>,
)((_: AssetsStateTree, id: string) => id);

const selectAssetFieldsGroup = createCachedSelector(
  (state: State) => state,
  selectAsset,
  (_state, _id, groupId) => groupId,
  (state, asset, groupId) =>
    selectOrganisationFieldGroup(state, asset.organisation_id, groupId),
)((_state: AssetsStateTree, id: string, groupId: string) => `${id}-${groupId}`);

export const selectAssetFieldResults = createCachedSelector(
  selectAsset,
  (_: AssetsStateTree, id: string) => id,
  (asset: Asset | undefined): Record<string, FieldResult> =>
    asset?.fieldResults || {},
)((_: AssetsStateTree, id: string) => id);

export const selectAssetFieldResultsForGroup = createCachedSelector(
  selectAssetFieldResults,
  selectAssetFieldsGroup,
  (results, group): Record<string, FieldResult> => {
    const filteredResults: Record<string, FieldResult> = {};
    group?.fieldIds.forEach((fieldId) => {
      if (results[fieldId]) {
        filteredResults[fieldId] = results[fieldId]!;
      }
    });
    return filteredResults;
  },
)((_: AssetsStateTree, id: string, groupId: string) => `${id}-${groupId}`);

export const selectAssetFieldRepeatableInstances = createCachedSelector(
  selectAsset,
  (state: State, assetId: string) =>
    selectOrganisationFieldGroups(
      state,
      selectAssetOrganisationId(state, assetId),
    ),
  (asset, groups) =>
    Object.values(asset.fieldInstances).reduce<
      Record<string, FieldInstanceWithFormattedAutoId>
    >((acc, instance) => {
      const group = groups[instance.groupId];
      if (
        group &&
        instance.action !== 'delete' &&
        instance.action !== 'remove'
      ) {
        acc[instance.instanceId] = {
          ...instance,
          action: instance.action,
          results: Object.entries(instance.results).reduce<
            Record<string, FieldResult>
          >((acc1, [fieldId, result]) => {
            const field = group.fields[fieldId];
            if (field && result.action !== 'delete') {
              acc1[fieldId] = result as FieldResult;
            }
            return acc1;
          }, {}),
          formattedAutoId: displayAutoId(
            instance.autoId!,
            group.autoIdZeroFill || 0,
            group.autoIdPrefix || '',
          ),
          inAssessment: false,
        };
      }
      return acc;
    }, {}),
)((_, assetId) => assetId);

export const selectAssetFieldRepeatableInstance = createCachedSelector(
  selectAssetFieldRepeatableInstances,
  (_: State, __: string, instanceId: string) => instanceId,
  (instances, instanceId) => instances[instanceId],
)((_, assetId, instanceId) => `${assetId}-${instanceId}`);

export const selectAssetFieldInstanceResultValue = createCachedSelector(
  selectAssetFieldRepeatableInstance,
  (_: State, __: string, ___: string, fieldId: string) => fieldId,
  (instance, fieldId): string | string[] | undefined =>
    instance?.results[fieldId]?.value || undefined,
)(
  (_: State, assetId: string, instanceId: string, fieldId: string) =>
    `${assetId}-${instanceId}-${fieldId}`,
);

export const selectAssetFieldResultValue = createCachedSelector(
  selectAssetFieldResults,
  (_: State, __: string, fieldId: string) => fieldId,
  (results, fieldId): string | string[] | undefined =>
    results[fieldId]?.value || undefined,
)((_: State, assetId: string, fieldId: string) => `${assetId}-${fieldId}`);

export const selectAssetFieldRepeatableInstancesForGroup = createCachedSelector(
  selectAssetFieldRepeatableInstances,
  (_: State, __: string, groupId: string) => groupId,
  (results, groupId) =>
    Object.entries(results).reduce<
      Record<string, FieldInstanceWithFormattedAutoId>
    >((acc, [instanceId, instance]) => {
      if (instance.groupId === groupId) {
        acc[instanceId] = instance;
      }
      return acc;
    }, {}),
)((_, assetId, groupId) => `${assetId}-${groupId}`);

export const selectAssetFieldRepeatableInstanceIdsForGroup =
  createCachedSelector(
    selectAssetFieldRepeatableInstancesForGroup,
    (instances): string[] => Object.keys(instances),
  )((_, assetId, groupId) => `${assetId}-${groupId}`);

export const selectAssetFieldRepeatableInstanceIdsForGroupFilteredAndSorted =
  createCachedSelector(
    selectAssetFieldRepeatableInstancesForGroup,
    (state: State, assetId: string, groupId: string) =>
      selectOrganisationFieldGroup(
        state,
        selectAssetOrganisationId(state, assetId),
        groupId,
      ),
    (state: State, assetId: UUID, groupId: string) =>
      selectGeneric(
        state,
        GENERICKEY_FIELDREPEATABLELIST({
          groupId,
          where: 'onasset',
          type: 'order',
        }),
      ) as FieldRepeatableOrderEnum | undefined,
    (state: State, assetId: UUID, groupId: string) =>
      selectGeneric(
        state,
        GENERICKEY_FIELDREPEATABLELIST({
          groupId,
          where: 'onasset',
          type: 'searchString',
        }),
      ) as string | undefined,
    (instances, group, orderValue, searchTerm): string[] => {
      // Filter
      let instancesArray = group
        ? Object.values(
            returnFieldInstanceIdsMatchingSearchTerm({
              group,
              fields: group.fields,
              instances,
              searchTerms: getSearchTermsFromSearchValue(searchTerm || ''),
            }),
          )
        : [];
      // Order
      const orderValueConfig =
        FieldRepeatableOrderConfig[
          (orderValue ? FieldRepeatableOrderEnum[orderValue] : null) ||
            FieldRepeatableOrderEnum.createdAsc
        ];
      if (orderValueConfig.type === 'meta') {
        instancesArray = orderBy(
          instancesArray,
          [orderValueConfig.key],
          [orderValueConfig.direction],
        );
      }
      // Return
      return instancesArray.map(({ instanceId }) => instanceId);
    },
  )((_, assetId, groupId) => `${assetId}-${groupId}`);

export const selectAssetFieldRepeatableGroupPins = createCachedSelector(
  (state: State, assetId: string, groupId: string) =>
    selectOrganisationFieldGroup(
      state,
      selectAssetOrganisationId(state, assetId),
      groupId,
    ),
  selectAssetFieldRepeatableInstancesForGroup,
  (group, instances): PinNewWithName[] => {
    if (!group) {
      return [];
    }
    const { pinFieldId } = group.calculatedInfo;
    if (!pinFieldId) {
      return [];
    }
    return Object.values(instances).reduce<PinNewWithName[]>(
      (acc, instance) => {
        const result = instance.results[pinFieldId];
        if (result?.pin) {
          acc.push({
            group: group.name,
            id: instance.instanceId,
            name: group.disableAutoId
              ? instance.reference || 'New'
              : instance.formattedAutoId!,
            planId: result.pin.planId,
            x: result.pin.x,
            y: result.pin.y,
          });
        }
        return acc;
      },
      [],
    );
  },
)((_, assetId, groupId) => `${assetId}-${groupId}`);

export const selectAssetFieldRepeatableNextAutoIdForGroup =
  createCachedSelector(
    selectAsset,
    selectAssetFieldRepeatableInstancesForGroup,
    (_: State, __: string, groupId: string) => groupId,
    (asset, instances, groupId) =>
      Math.max(
        asset.fieldRepeatableAutoIds?.[groupId] || 1,
        ...Object.values(instances).map((i) => (i.autoId || 1) + 1),
      ),
  )((_: State, assetId: string, groupId: string) => `${assetId}-${groupId}`);

export const selectAssetAssessments = createCachedSelector(
  selectAsset,
  (_: AssetsStateTree, id: string) => id,
  (asset) => asset.assessments,
)((_: AssetsStateTree, id: string) => id);

export const selectChosenAssetIds = createSelector(
  selectAssetsState,
  (assets: AssetsState): string[] => assets.chosen,
);

/** PLANS */

export const selectAssetPlans = createCachedSelector(
  selectAsset,
  (asset: Asset) =>
    Object.entries(asset.plans).reduce<Record<string, AssetPlan>>(
      (acc, [planId, plan]) => {
        if (plan) {
          acc[planId] = plan;
        }
        return acc;
      },
      {},
    ),
)((_: AssetsStateTree, id: string) => id);

export const selectAssetPlan = createCachedSelector(
  selectAssetPlans,
  (_, __, planId: string) => planId,
  (plans, planId) => plans[planId],
)(
  (_: AssetsStateTree, assetId: string, planId: string) =>
    `${assetId}-${planId}`,
);

/** HOME LIST AND FILTER STATE */

const selectAssetsListAsArray = createSelector(
  selectAssetListUnmerged,
  (assetList): AbridgedAsset[] => Object.values(assetList),
);
export const selectChosenAssetsCount = createSelector(
  selectChosenAssetIds,
  (chosen: string[]): number => chosen.length,
);

const selectAssetListFilter = createSelector(
  selectAssetsState,
  (assets: AssetsState): AssetsFilter => assets.filter,
);

export const selectOrganisationFilterValue = createSelector(
  selectAssetsState,
  (assets: AssetsState): string | undefined => assets.filter.organisations[0],
);

export const selectAssetListSearchText = createSelector(
  selectAssetListFilter,
  (filter: AssetsFilter): string => filter.search,
);

export const selectAssetListSort = createSelector(
  selectAssetsState,
  (assets: AssetsState): AssetsSortOrder => assets.sort,
);

const selectFilteredAndSortedAssetsListItems = createSelector(
  selectAssetsListAsArray,
  selectOrganisationsList,
  selectAssetListFilter,
  selectAssetListSort,
  filterAndSortAssets,
);

export const selectFilteredAssetsListIds = createSelector(
  selectFilteredAndSortedAssetsListItems,
  (assets: AbridgedAsset[]): string[] => assets.map(({ id }) => id),
);
export const selectFilteredAssetsListTotal = createSelector(
  selectFilteredAndSortedAssetsListItems,
  (assets: AbridgedAsset[]): number => assets.length,
);

export const selectIsAssetChosen = createCachedSelector(
  selectChosenAssetIds,
  (_: AssetsStateTree, id: string) => id,
  (chosen: string[], id: string): boolean => chosen.includes(id),
)((_: AssetsStateTree, id: string) => id);
